import StarIcon from '@mui/icons-material/Star';
import { Box } from '@mui/material';
import { MatchStatus, Role, TournamentStatus } from '@okletsplay/core';
import { MatchEntity, TournamentEntity } from 'app/interfaces/entity';
import { updateMatch } from 'modules/matches/store/matches/repository';
import { updateTournamentProperties } from 'modules/tournaments/store/tournaments/repository';
import React, { FC, memo, MouseEvent, useCallback, useMemo } from 'react';
import { Loader } from 'shared/components/Loader';
import { withFeatured } from 'shared/HOC/withFeatured';
import { withMatchStatus } from 'shared/HOC/withMatchStatus';
import { withRoles } from 'shared/HOC/withRoles';
import { withTournamentStatus } from 'shared/HOC/withTournamentStatus';
import { useStyles } from './FeaturedButton.styles';

interface FeaturedButtonProps<T = MatchEntity | TournamentEntity> {
  entity: T;
  value: boolean;
  isLoading?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>, data: T) => void;
}

export const FeaturedButtonComponent: FC<FeaturedButtonProps> = ({
  value,
  entity,
  isLoading,
  onClick,
}) => {
  const { classes } = useStyles();
  const color = useMemo(() => (value ? 'primary' : 'action'), [value]);

  const onClickHandler = useCallback(
    (event) => {
      onClick?.(event, entity);
    },
    [onClick, entity]
  );

  return (
    <Box className={classes.root}>
      {isLoading ? (
        <Loader size={28} />
      ) : (
        <StarIcon className={classes.icon} onClick={onClickHandler} color={color} />
      )}
    </Box>
  );
};

export const MatchFeaturedButton = memo(
  withMatchStatus<{ entity: MatchEntity; value: boolean }>(
    withFeatured(withRoles(FeaturedButtonComponent, [Role.MatchAdministrator]), updateMatch),
    [MatchStatus.Pending, MatchStatus.Created, MatchStatus.Lobby, MatchStatus.Live]
  )
);

export const TournamentFeaturedButton = memo(
  withTournamentStatus(
    withFeatured(
      withRoles(FeaturedButtonComponent, [Role.TournamentAdministrator]),
      updateTournamentProperties
    ),
    [TournamentStatus.Pending, TournamentStatus.Checkin, TournamentStatus.Live]
  )
);
